import { useState, useEffect } from "react";
import { Bell, X, CheckCircle2, AlertTriangle, Info, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Alert {
  id: string;
  alert_type: string;
  title: string;
  body: string | null;
  severity: string;
  read_by: string[];
  created_at: string;
}

const severityIcon = (s: string) => {
  switch (s) {
    case "critical": return <Shield className="h-3.5 w-3.5 text-destructive flex-shrink-0" />;
    case "warning": return <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />;
    default: return <Info className="h-3.5 w-3.5 text-primary flex-shrink-0" />;
  }
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchAlerts();

    const channel = supabase
      .channel("alerts-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "alerts" }, (payload) => {
        setAlerts((prev) => [payload.new as Alert, ...prev].slice(0, 50));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const fetchAlerts = async () => {
    const { data } = await supabase
      .from("alerts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (data) setAlerts(data as Alert[]);
  };

  const unreadCount = alerts.filter((a) => !a.read_by?.includes(user?.id || "")).length;

  const markRead = async (alertId: string) => {
    if (!user) return;
    const alert = alerts.find((a) => a.id === alertId);
    if (!alert || alert.read_by?.includes(user.id)) return;
    const newReadBy = [...(alert.read_by || []), user.id];
    await supabase.from("alerts").update({ read_by: newReadBy } as any).eq("id", alertId);
    setAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, read_by: newReadBy } : a));
  };

  const markAllRead = async () => {
    if (!user) return;
    for (const a of alerts.filter((a) => !a.read_by?.includes(user.id))) {
      await markRead(a.id);
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-[10px] text-destructive-foreground flex items-center justify-center font-bold">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No notifications yet</p>
          ) : (
            alerts.slice(0, 20).map((a) => {
              const isRead = a.read_by?.includes(user?.id || "");
              return (
                <div
                  key={a.id}
                  className={`px-4 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-muted/30 transition-colors ${
                    !isRead ? "bg-primary/5" : ""
                  }`}
                  onClick={() => markRead(a.id)}
                >
                  <div className="flex items-start gap-2.5">
                    {severityIcon(a.severity)}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium ${!isRead ? "text-foreground" : "text-muted-foreground"}`}>
                        {a.title}
                      </p>
                      {a.body && (
                        <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{a.body}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {new Date(a.created_at).toLocaleString()}
                      </p>
                    </div>
                    {!isRead && <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1" />}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
